import React, { useEffect, useState } from 'react';
import api from '../api';

const TYPE_CONFIG = {
  import_csv:    { label: 'Import CSV',      bg: '#f0fdf4', color: '#16a34a', border: '#86efac' },
  bank_sync:     { label: 'Sync banque',     bg: '#eff6ff', color: '#2563eb', border: '#93c5fd' },
  login:         { label: 'Connexion',       bg: '#f0f9ff', color: '#0284c7', border: '#7dd3fc' },
  login_failed:  { label: 'Échec connexion', bg: '#fef2f2', color: '#dc2626', border: '#fca5a5' },
  tx_create:     { label: 'Ajout manuel',    bg: '#f7fee7', color: '#65a30d', border: '#bef264' },
  tx_delete:     { label: 'Suppression',     bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
  tx_categorize: { label: 'Catégorie',       bg: '#fdf4ff', color: '#9333ea', border: '#d8b4fe' },
  tx_split:      { label: 'Scission',        bg: '#fefce8', color: '#ca8a04', border: '#fde047' },
  sync_config:   { label: 'Config sync',     bg: '#f0f9ff', color: '#0369a1', border: '#7dd3fc' },
  delete:        { label: 'Suppression',     bg: '#fff7ed', color: '#ea580c', border: '#fdba74' },
  categorize:    { label: 'Catégorie',       bg: '#fdf4ff', color: '#9333ea', border: '#d8b4fe' },
};

function typeConfig(type) {
  return TYPE_CONFIG[type] || { label: type, bg: '#f8fafc', color: '#64748b', border: '#cbd5e1' };
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function TxList({ rows, color }) {
  if (!rows || rows.length === 0) return null;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 6 }}>
      <tbody>
        {rows.map((tx, i) => (
          <tr key={i} style={{ borderBottom: '1px solid var(--border-soft)' }}>
            <td style={{ padding: '3px 6px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {tx.date ? new Date(tx.date).toLocaleDateString('fr-FR') : '—'}
            </td>
            <td style={{ padding: '3px 6px', flex: 1 }}>
              {tx.merchant || tx.label || tx.rawLabel || '—'}
            </td>
            <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', color }}>
              {tx.amount > 0 ? '+' : ''}{Number(tx.amount).toFixed(2)} €
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetailsPanel({ details }) {
  const { inserted, skipped, date_from, insertedList, skippedList, ...rest } = details;
  const hasTxLists = insertedList || skippedList;

  return (
    <div>
      {/* Stats résumé */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: hasTxLists ? 10 : 0 }}>
        {inserted !== undefined && <span><span style={{ color: 'var(--text-muted)' }}>insérées : </span><strong>{inserted}</strong></span>}
        {skipped  !== undefined && <span><span style={{ color: 'var(--text-muted)' }}>ignorées : </span><strong>{skipped}</strong></span>}
        {date_from && <span><span style={{ color: 'var(--text-muted)' }}>depuis : </span><strong>{date_from}</strong></span>}
        {Object.entries(rest).filter(([, v]) => !Array.isArray(v)).map(([k, v]) => (
          <span key={k}><span style={{ color: 'var(--text-muted)' }}>{k} : </span><strong>{String(v)}</strong></span>
        ))}
      </div>

      {/* Nouvelles transactions */}
      {insertedList && insertedList.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontWeight: 600, color: '#16a34a', marginBottom: 2 }}>
            ✓ {insertedList.length} ajoutée{insertedList.length > 1 ? 's' : ''}
          </div>
          <TxList rows={insertedList} color="#16a34a" />
        </div>
      )}

      {/* Doublons ignorés */}
      {skippedList && skippedList.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, color: 'var(--text-muted)', marginBottom: 2 }}>
            — {skippedList.length} doublon{skippedList.length > 1 ? 's' : ''}
          </div>
          <TxList rows={skippedList} color="var(--text-muted)" />
        </div>
      )}
    </div>
  );
}

export default function ActivityLog({ refreshKey }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get('/activity?limit=100')
      .then(r => setEntries(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refreshKey]);

  if (loading) {
    return <div style={{ padding: 32, color: 'var(--text-muted)', textAlign: 'center' }}>Chargement…</div>;
  }

  if (entries.length === 0) {
    return (
      <div style={{ padding: 32, color: 'var(--text-muted)', textAlign: 'center' }}>
        Aucune activité enregistrée
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>Journal d'activité</h2>
        <span style={{ fontSize: 13, color: 'var(--text-soft)' }}>
          {entries.length} entrée{entries.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {entries.map(entry => {
          const cfg = typeConfig(entry.type);
          const isOpen = expanded === entry.id;
          const details = entry.details;

          return (
            <div
              key={entry.id}
              style={{
                background: 'var(--card)',
                border: '1px solid var(--border-soft)',
                borderRadius: 'var(--radius)',
                padding: '12px 16px',
                cursor: details ? 'pointer' : 'default',
              }}
              onClick={() => details && setExpanded(isOpen ? null : entry.id)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                  background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
                  whiteSpace: 'nowrap',
                }}>
                  {cfg.label}
                </span>

                <span style={{ flex: 1, fontSize: 14 }}>{entry.message}</span>

                <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {formatDate(entry.created_at)}
                </span>

                {details && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                )}
              </div>

              {isOpen && details && (
                <div style={{
                  marginTop: 10, padding: '10px 12px',
                  background: 'var(--bg)', borderRadius: 'var(--radius)',
                  fontSize: 12, color: 'var(--text-soft)',
                }}>
                  <DetailsPanel details={details} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
